<template>
  <v-card-text class="d-flex flex-column align-center mb-4">
    <ProgressCircular />
    <div>{{ t('content-creator.license-image.loading') }}</div>
  </v-card-text>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import ProgressCircular from '@/components/ProgressCircular.vue'
import { showSnackbarMessage } from '@/lib/snackbarMessages'

import { useIsStockPhotoLicensed } from './queries/isStockPhotoLicensed/useIsStockPhotoLicensed'
import { useLicenseStockPhoto } from './queries/licenseStockPhoto/useLicenseStockPhoto'

interface Props {
  imageSourceVariant: string
  stockPhotoId: string
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  (event: 'imageUrl', url: string): void
  (event: 'error', error: Error): void
}>()

const { t } = useI18n()
const { checkLicense } = useIsStockPhotoLicensed()
const { licenseStockPhoto } = useLicenseStockPhoto()

const checkStockPhotoLicense = async (): Promise<void> => {
  try {
    const { isLicensed, url } = await checkLicense({
      stockPhotoId: props.stockPhotoId,
      provider: props.imageSourceVariant,
    })

    if (isLicensed && url) {
      return emit('imageUrl', url)
    }

    const license = await licenseStockPhoto({
      stockPhotoId: props.stockPhotoId,
      provider: props.imageSourceVariant,
    })
    if (!license.url) {
      throw new Error('No license image found')
    }
    return emit('imageUrl', license.url)
  } catch (error) {
    showSnackbarMessage('error', t('content-creator.error.license-image'))
    emit('error', error as Error)
    throw error
  }
}

onMounted(() => {
  checkStockPhotoLicense()
})
</script>
